import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import { useHttpClient } from '../../shared/hooks/http-hook.ts';
import CircularSlider from '../components/CircularSlider.js';
import Journal from '../components/Journal.js';
import NumberInput from '../components/NumberInput.tsx';
import NutritionList from '../components/NutritionList.js';
import TrackerHeading from '../components/TrackerHeading.js';

function NutritionTracker(props) {
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [date, setDate] = useState(false);
  const [data, setData] = useState(null);
  const [journal, setJournal] = useState(null);
  const [currentTracker, setCurrentTracker] = useState(null);
  const [takeaways, setTakeaways] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [resetLines, setResetLines] = useState(false);

  useEffect(() => {
    setData((prevState) => {
      return {
        ...prevState,
        takeaways,
      };
    });
  }, [takeaways]);

  const getData = async (date) => {
    if (date.length > 0) {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/trackers/nutrition/${date}`
        );

        if (response && response.tracker?._id) {
          const { tracker } = response;
          setCurrentTracker({
            id: tracker._id,
            trackerDate: tracker.tracker_date.toString().slice(0, 10),
          });
          const data = JSON.parse(tracker.data);
          setData(data);
          setStartTime(data.first_meal);
          setEndTime(data.last_meal);
          setTakeaways(data.takeaways);
          setJournal({
            enhancers: tracker.enhancers,
            inhibitors: tracker.inhibitors,
            journal: tracker.journal,
          });
          setIsSaved(true);
        } else {
          setCurrentTracker({
            id: '',
            trackerDate: date,
          });
          setData({
            first_meal: '08:00',
            last_meal: '18:00',
            vegetables: '',
            takeaways: '',
          });
          setStartTime('08:00');
          setEndTime('18:00');
          setTakeaways(0);
          setJournal(() => ({
            enhancers: [],
            inhibitors: [],
            journal: '',
          }));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    setResetLines(true);
    getData(date);
  }, [date]);

  const saveResponse = (location, stat) => {
    if (location === 'journal') {
      setJournal((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    } else {
      setData((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
      if (stat.id === 'first_meal') {
        setStartTime(stat.value);
      }
      if (stat.id === 'last_meal') {
        setEndTime(stat.value);
      }
    }
  };

  const sendData = async () => {
    let method = 'POST';
    let url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;
    if (currentTracker.id !== '') {
      method = 'PUT';
      url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${currentTracker.id}`;
    }

    try {
      const response = await sendRequest(
        url,
        method,
        JSON.stringify({
          trackerDate: date,
          type: 'nutrition',
          data,
          hidden: false,
          enhancers: [],
          inhibitors: [],
          journal: journal.journal,
          platform: 'web',
        })
      );
      if (response) {
        setIsSaved(true);
        props.closeTrackers();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendData();
  };

  return (
    <div
      id="snm-nutrition"
      className="tracker-container"
      role="region"
      aria-label={`${t('T349')} ${t('tracker.region.title')}`}
    >
      <form className="tracker-form" onSubmit={handleSubmit} noValidate={false}>
        <div className="trackers-inner">
          <div className="back-to-main">
            <button
              className="back-btn"
              aria-label={t('A136')}
              title={t('A136')}
              onClick={props.handleBackToMain}
              type="button"
            >
              <FontAwesomeIcon icon={faCircleChevronLeft} />
            </button>
          </div>
          <TrackerHeading title="T051" setDate={setDate} isSaved={isSaved} />
          <div className="trackers-body" role="group" aria-label={t('modal.tracker.details')}>
            <fieldset className="ind-tracker no-padding">
              <legend className="title">{t('T052')}</legend>

              {data?.first_meal && data?.last_meal && (
                <CircularSlider
                  meals
                  questionid={['first_meal', 'last_meal']}
                  startTime={startTime}
                  endTime={endTime}
                  data={data}
                  saveResponse={saveResponse}
                  title={t('T052')}
                />
              )}
              <input
                type="hidden"
                name="first_meal"
                value={data?.first_meal || ''}
                aria-label={t('First meal time')}
              />
              <input
                type="hidden"
                name="last_meal"
                value={data?.last_meal || ''}
                aria-label={t('Last meal time')}
              />
            </fieldset>

            <fieldset className="ind-tracker no-padding">
              <legend className="title title-with-border">{t('T054')}</legend>
              <div className="buffer" />
              <NutritionList
                questionid="vegetables"
                data={data}
                saveResponse={saveResponse}
                resetLines={resetLines}
                setResetLines={setResetLines}
              />
              <input
                type="hidden"
                name="vegetables"
                value={data?.vegetables || ''}
                aria-label={t('T054')}
              />
            </fieldset>
            <div className="ind-tracker">
              <fieldset className="number-input-section no-padding">
                <legend className="title title-with-padding title-with-float">{t('T057')}</legend>
                <NumberInput
                  inputName="takeaways"
                  startValue={takeaways}
                  minValue={0}
                  maxValue={30}
                  step={1}
                  onSetValue={(value) => {
                    setTakeaways(value);
                  }}
                  controlsInfo={t('T057')}
                  type="padding-top"
                />
              </fieldset>
              <div className="buffer" />
              <input type="hidden" name="takeaways" value={takeaways || 0} aria-label={t('T057')} />
            </div>
            <div className="ind-tracker" role="group" aria-label={`${t('T349')} - ${t('T018')}`}>
              <Journal
                placeholder={t('T020')}
                questionid="journal"
                data={journal}
                saveResponse={saveResponse}
              />
            </div>
          </div>
        </div>
        <div className="buttons">
          <Button onClick={props.closeTrackers} variant="secondary" color="dark" type="button">
            {t('T015')}
          </Button>
          <Button type="submit" icon={{ faIconDefinition: faArrowRight }} iconPosition="right">
            {t('T016')}
          </Button>
        </div>
      </form>
    </div>
  );
}
export default NutritionTracker;
